import { Injectable, inject } from '@angular/core';
import { BlockStatementNode } from '../ast/block-statement';
import { ContentStatementNode } from '../ast/content-statement';
import { MustacheStatementNode } from '../ast/mustache-statement';
import { PathExpressionNode } from '../ast/path-expression';
import { ProgramNode } from '../ast/program';
import { TemplateVariable } from '../interfaces/template-variable.interface';
import { SELECTORS } from '../utils/constants';
import { CursorManagementService } from './cursor-management.service';

@Injectable()
export class ElementInsertionService {
  private cursorService = inject(CursorManagementService);

  /**
   * Insere um helper de bloco no editor.
   */
  public insertBlock(
    variable: TemplateVariable,
    term: string,
    trigger: { char: string },
    editorElement: HTMLElement,
    knownVariables: Map<string, TemplateVariable>,
  ): void {
    const path = new PathExpressionNode(variable.path);
    const program = new ProgramNode([new ContentStatementNode('')]);
    const blockNode = new BlockStatementNode(path, [], program, null);
    const domNode = BlockStatementNode.toDomFragment(blockNode, knownVariables);

    this.insertNode(domNode, term, trigger, false);

    const insertedBlock = editorElement.querySelector(`[data-block-helper="${variable.path}"]`);
    if (insertedBlock) {
      this.cursorService.focusFirstArgument(insertedBlock);
    }
  }

  /**
   * Insere um chip de variável ou função no editor.
   */
  public insertMustache(variable: TemplateVariable, term: string, trigger: { char: string }): void {
    const chip = MustacheStatementNode.toDomNode(variable);
    const isFunction = variable.type === 'function';
    this.insertNode(chip, term, trigger, isFunction);
  }

  /**
   * Insere uma variável como um argumento de uma função ou bloco.
   */
  public insertVariableIntoArgument(
    variable: TemplateVariable,
    argSpan: HTMLElement,
    term: string,
    trigger: { char: string },
  ): void {
    const chip = MustacheStatementNode.toDomNode(variable);
    const selection = window.getSelection()!;
    const range = selection.getRangeAt(0);

    range.setStart(range.startContainer, range.startOffset - term.length - trigger.char.length);
    range.deleteContents();

    argSpan.innerHTML = '';
    argSpan.append(chip);
    argSpan.classList.remove('is-placeholder');

    if (!this.cursorService.focusNextArgument(argSpan)) {
      const functionWrapper = argSpan.closest(
        `${SELECTORS.CHIP_WRAPPER}, ${SELECTORS.BLOCK_STATEMENT}`,
      );
      if (functionWrapper) {
        this.cursorService.setCursorAfterElement(functionWrapper);
      }
    }
  }

  /**
   * Remove um elemento (chip ou bloco) do editor.
   */
  public deleteElement(element: HTMLElement, editorElement: HTMLElement): boolean {
    if (editorElement.contains(element)) {
      element.remove();
      return true;
    }

    return false;
  }

  /**
   * Lógica genérica para inserir um nó do DOM no editor na posição do cursor.
   */
  private insertNode(
    node: Node,
    term: string,
    trigger: { char: string },
    focusFirstArgument = false,
  ): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    const deleteLength = term.length + trigger.char.length;
    const newOffset = Math.max(0, range.startOffset - deleteLength);
    range.setStart(range.startContainer, newOffset);
    range.deleteContents();

    range.insertNode(node);
    const lastNodeToFocus = node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.lastChild : node;

    if (lastNodeToFocus) {
      range.setStartAfter(lastNodeToFocus);
    }

    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    if (focusFirstArgument) {
      const container =
        node.nodeType === Node.DOCUMENT_FRAGMENT_NODE
          ? (node as DocumentFragment)
          : (node as Element);
      const focusable = container.querySelector(SELECTORS.CONTENT_EDITABLE) as HTMLElement;
      if (focusable) {
        range.selectNodeContents(focusable);
        if (focusable.classList.contains('is-placeholder')) {
          range.collapse(true);
        }

        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
}
